/**
 * Configuración de aplicaciones para AdminConsole
 * Define todas las aplicaciones disponibles y su configuración
 */

import {
  ShoppingCartOutlined,
  MailOutlined,
  CollectionsOutlined,
  SettingsOutlined,
  AttachMoneyOutlined,
  StorageOutlined,
  ExtensionOutlined,
  MemoryOutlined,
  ManageAccountsOutlined,
  CampaignOutlined,
  HelpOutline,
  ReportProblemOutlined,
  HandshakeOutlined,
} from '@mui/icons-material';

/**
 * Lista completa de aplicaciones del AdminConsole
 * 
 * Cada aplicación debe tener:
 * - id: Identificador único
 * - name: Nombre para mostrar
 * - path: Ruta de React Router
 * - icon: Componente de icono de MUI
 * - color: Color en formato hexadecimal
 * - pinned: Si aparece en la barra de navegación
 * - order: Orden de visualización (menor = más a la izquierda)
 * - description: Descripción breve (opcional)
 * - category: Categoría de la app (opcional)
 */
export const adminApps = [
  {
    id: 'coupons',
    name: 'Coupons',
    path: '/coupons',
    icon: ShoppingCartOutlined,
    color: '#2196f3',
    pinned: true,
    order: 0,
    description: 'Gestión de cupones y promociones',
    category: 'commerce',
  },
  {
    id: 'contact',
    name: 'Contact',
    path: '/contact',
    icon: MailOutlined,
    color: '#f44336',
    pinned: true,
    order: 1,
    description: 'Gestión de contactos y mensajes',
    category: 'communication',
  },
  {
    id: 'collections',
    name: 'Collections',
    path: '/collections',
    icon: CollectionsOutlined,
    color: '#9c27b0',
    pinned: false,
    hidden: true,
    order: 2,
    description: 'Administración de colecciones',
    category: 'content',
  },
  {
    id: 'settings',
    name: 'Settings',
    path: '/settings',
    icon: SettingsOutlined,
    color: '#ff9800',
    pinned: true,
    order: 3,
    description: 'Configuración del sistema',
    category: 'system',
  },
  {
    id: 'credit-package',
    name: 'Credit',
    path: '/credit-package',
    icon: AttachMoneyOutlined,
    color: '#4caf50',
    pinned: true,
    order: 4,
    description: 'Paquetes de crédito y facturación',
    category: 'billing',
  },
  {
    id: 'storage',
    name: 'Storage',
    path: '/storage',
    icon: StorageOutlined,
    color: '#00bcd4',
    pinned: true,
    order: 5,
    description: 'Gestión de almacenamiento',
    category: 'infrastructure',
  },
  {
    id: 'extensions',
    name: 'Extensions',
    path: '/extensions',
    icon: ExtensionOutlined,
    color: '#673ab7',
    pinned: true,
    order: 6,
    description: 'Extensiones y plugins',
    category: 'system',
  },
  {
    id: 'ai-studio',
    name: 'AI Studio',
    path: '/ai-studio',
    icon: MemoryOutlined,
    color: '#e91e63',
    pinned: true,
    order: 7,
    description: 'Estudio de inteligencia artificial',
    category: 'ai',
  },
  {
    id: 'plan-control',
    name: 'Plan Control',
    path: '/PlanControl',
    icon: ManageAccountsOutlined,
    color: '#3f51b5',
    pinned: true,
    order: 8,
    description: 'Control de planes y suscripciones',
    category: 'billing',
  },
  {
    id: 'stripe-provisioning-failures',
    name: 'Provisioning Failures',
    path: '/stripe-provisioning-failures',
    icon: ReportProblemOutlined,
    color: '#d32f2f',
    pinned: true,
    order: 9,
    description: 'Stripe provisioning failures and retries',
    category: 'support',
  },
  // Apps no fijadas por defecto
  {
    id: 'campaigns',
    name: 'Campaigns',
    path: '/campaigns',
    icon: CampaignOutlined,
    color: '#ff5722',
    pinned: false,
    order: 10,
    description: 'Gestión de campañas',
    category: 'marketing',
  },
  {
    id: 'agreements',
    name: 'Agreements',
    path: '/agreements',
    icon: HandshakeOutlined,
    color: '#795548',
    pinned: true,
    order: 11,
    description: 'Manage deposit agreements',
    category: 'billing',
  },
  {
    id: 'help',
    name: 'Help',
    path: '/help',
    icon: HelpOutline,
    color: '#607d8b',
    pinned: false,
    hidden: true,
    order: 12,
    description: 'Centro de ayuda y soporte',
    category: 'support',
  },
];

/**
 * Obtener aplicaciones fijadas ordenadas
 * @returns {Array} Array de aplicaciones fijadas
 */
export const getPinnedApps = () => {
  return adminApps
    .filter(app => app.pinned)
    .sort((a, b) => a.order - b.order);
};

/**
 * Obtener todas las aplicaciones ordenadas
 * @returns {Array} Array de todas las aplicaciones
 */
export const getAllApps = () => {
  return [...adminApps].sort((a, b) => a.order - b.order);
};

/**
 * Encontrar una aplicación por ID
 * @param {string} id - ID de la aplicación
 * @returns {Object|undefined} Objeto de aplicación o undefined
 */
export const getAppById = (id) => {
  return adminApps.find(app => app.id === id);
};

/**
 * Encontrar una aplicación por ruta
 * @param {string} path - Ruta de la aplicación
 * @returns {Object|undefined} Objeto de aplicación o undefined
 */
export const getAppByPath = (path) => {
  // Normalizar path (remover trailing slash)
  const normalizedPath = path.endsWith('/') && path !== '/' 
    ? path.slice(0, -1) 
    : path;
  
  return adminApps.find(app => app.path === normalizedPath);
};

/**
 * Obtener aplicaciones por categoría
 * @param {string} category - Categoría de aplicaciones
 * @returns {Array} Array de aplicaciones en esa categoría
 */
export const getAppsByCategory = (category) => {
  return adminApps.filter(app => app.category === category);
};

/**
 * Categorías disponibles
 */
export const appCategories = {
  commerce: { name: 'Comercio', color: '#2196f3' },
  communication: { name: 'Comunicación', color: '#f44336' },
  content: { name: 'Contenido', color: '#9c27b0' },
  system: { name: 'Sistema', color: '#ff9800' },
  billing: { name: 'Facturación', color: '#4caf50' },
  infrastructure: { name: 'Infraestructura', color: '#00bcd4' },
  ai: { name: 'IA', color: '#e91e63' },
  marketing: { name: 'Marketing', color: '#ff5722' },
  support: { name: 'Soporte', color: '#607d8b' },
};

/**
 * Configuración por defecto de la navegación
 */
export const defaultNavigationSettings = {
  showHomeDashboard: true,
  showSearchBox: true,
  showAppLauncher: true,
  showStarShortcut: false,
  showLanguageSelector: false,
  showFullscreenToggle: false,
  showFontSizeControl: false,
  topBarIconStyle: 'colored', // 'colored' | 'minimal'
  layoutStyle: 'auto-detect', // 'horizontal' | 'vertical' | 'auto-detect'
};

/**
 * Guardar orden personalizado de apps
 * @param {Array} orderedAppIds - Array de IDs en el nuevo orden
 */
export const saveAppOrder = (orderedAppIds) => {
  try {
    localStorage.setItem('admin-app-order', JSON.stringify(orderedAppIds));
    return true;
  } catch (error) {
    console.error('Error guardando orden de apps:', error);
    return false;
  }
};

/**
 * Cargar orden personalizado de apps
 * @returns {Array|null} Array de IDs ordenados o null
 */
export const loadAppOrder = () => {
  try {
    const savedOrder = localStorage.getItem('admin-app-order');
    return savedOrder ? JSON.parse(savedOrder) : null;
  } catch (error) {
    console.error('Error cargando orden de apps:', error);
    return null;
  }
};

/**
 * Obtener apps fijadas con orden personalizado
 * @returns {Array} Array de aplicaciones fijadas con orden personalizado
 */
export const getPinnedAppsWithCustomOrder = () => {
  const pinnedApps = getPinnedApps();
  const savedOrder = loadAppOrder();
  
  if (!savedOrder) {
    return pinnedApps;
  }

  // Aplicar orden personalizado
  const orderedApps = [];
  const appMap = new Map(pinnedApps.map(app => [app.id, app]));
  
  savedOrder.forEach(id => {
    if (appMap.has(id)) {
      orderedApps.push(appMap.get(id));
      appMap.delete(id);
    }
  });
  
  // Agregar apps que no están en el orden guardado
  appMap.forEach(app => orderedApps.push(app));
  
  return orderedApps;
};

/**
 * Toggle pin status de una app
 * @param {string} appId - ID de la aplicación
 * @returns {boolean} Nuevo estado de pinned
 */
export const toggleAppPin = (appId) => {
  const app = getAppById(appId);
  if (!app) return false;
  
  app.pinned = !app.pinned;
  
  // Guardar en localStorage
  try {
    const pinnedIds = getPinnedApps().map(a => a.id);
    localStorage.setItem('admin-pinned-apps', JSON.stringify(pinnedIds));
  } catch (error) {
    console.error('Error guardando apps fijadas:', error);
  }
  
  return app.pinned;
};

export default adminApps;
